import type {
  RelatorioTemposItem,
  RelatorioTemposLinha,
  RelatorioTemposRankingLinha,
  RelatorioTemposResponse,
  RelatorioTemposSetor,
  RelatorioTemposSetorEntrega,
  RelatorioTemposUsuario,
  RelatorioTemposUsuarioItem,
} from "../../../services/controleProducaoService";
import { groupByUndNome, rotuloUnd } from "../RelatorioConsumo/groupByUnd";

export type FiltrosRelatorioTempos = {
  setorId: number | "";
  userId: number | "";
  itemId: number | "";
  undNome: string;
};

function formatTempoMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
}

type RankingOpts<T> = {
  id: (row: T) => number;
  nome: (row: T) => string;
  valor: (row: T) => number;
  format: (row: T, valor: number) => string;
  detalhe?: (row: T) => string | undefined;
  ocorrencias: (row: T) => number;
  minAmostras?: number;
  ordem: "asc" | "desc";
  limite?: number;
  undNome?: (row: T) => string | undefined;
};

function rankingDe<T>(
  rows: T[],
  opts: RankingOpts<T>
): RelatorioTemposRankingLinha[] {
  const min = opts.minAmostras ?? 0;
  const limite = opts.limite ?? 8;
  return rows
    .filter(
      (row) => opts.ocorrencias(row) >= min && Number.isFinite(opts.valor(row))
    )
    .sort((a, b) => {
      const va = opts.valor(a);
      const vb = opts.valor(b);
      return opts.ordem === "asc" ? va - vb : vb - va;
    })
    .slice(0, limite)
    .map((row, index) => {
      const valor = opts.valor(row);
      const undNome = opts.undNome?.(row);
      return {
        posicao: index + 1,
        id: opts.id(row),
        nome: opts.nome(row),
        ocorrencias: opts.ocorrencias(row),
        valor,
        valorFormatado: opts.format(row, valor),
        detalhe: opts.detalhe?.(row),
        undNome: undNome ? rotuloUnd(undNome) : undefined,
      };
    });
}

function rankingPorUnd<T>(
  rows: T[],
  getUnd: (row: T) => string | null | undefined,
  opts: RankingOpts<T>
): RelatorioTemposRankingLinha[] {
  return groupByUndNome(rows, getUnd).flatMap((grupo) =>
    rankingDe(grupo.items, { ...opts, undNome: () => grupo.undNome })
  );
}

function itemPassaFiltro(
  item: RelatorioTemposUsuarioItem,
  filtros: FiltrosRelatorioTempos,
  setorNome: string | undefined
): boolean {
  if (filtros.itemId !== "" && item.itemId !== filtros.itemId) return false;
  if (filtros.undNome && rotuloUnd(item.undNome) !== filtros.undNome) {
    return false;
  }
  if (filtros.setorId === "") return true;
  if (item.setorIds?.includes(filtros.setorId)) return true;
  if (setorNome && item.setores?.includes(setorNome)) return true;
  return false;
}

function recomputarUsuario(
  usuario: RelatorioTemposUsuario,
  porItem: RelatorioTemposUsuarioItem[]
): RelatorioTemposUsuario {
  const tempoTotalMs = porItem.reduce(
    (acc, item) => acc + item.tempoTotalMs,
    0
  );
  const tempoPausaMs = porItem.reduce(
    (acc, item) => acc + (item.tempoPausaMs ?? 0),
    0
  );
  const ocorrencias = porItem.reduce((acc, item) => acc + item.ocorrencias, 0);
  const temposMedios = porItem.map((item) => item.tempoMedioMs);
  const tempoMedioMs = ocorrencias ? Math.round(tempoTotalMs / ocorrencias) : 0;
  const tempoMinMs = temposMedios.length ? Math.min(...temposMedios) : 0;
  const tempoMaxMs = temposMedios.length ? Math.max(...temposMedios) : 0;
  const undMap = new Map<string, number>();
  for (const item of porItem) {
    if (item.producao != null && item.producao > 0 && item.undNome) {
      undMap.set(item.undNome, (undMap.get(item.undNome) ?? 0) + item.producao);
    }
  }
  const producaoPorUnd = Array.from(undMap.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([undNome, total]) => ({
      undNome,
      total,
      totalFormatado: `${total.toLocaleString("pt-BR", {
        maximumFractionDigits: 3,
      })} ${undNome}`,
    }));
  return {
    ...usuario,
    porItem,
    itensDistintos: porItem.length,
    ocorrencias,
    tempoTotalMs,
    tempoFormatado: formatTempoMs(tempoTotalMs),
    tempoPausaMs,
    tempoPausaFormatado: formatTempoMs(tempoPausaMs),
    tempoMedioMs,
    tempoMedioFormatado: formatTempoMs(tempoMedioMs),
    tempoMinMs,
    tempoMinFormatado: formatTempoMs(tempoMinMs),
    tempoMaxMs,
    tempoMaxFormatado: formatTempoMs(tempoMaxMs),
    producaoPorUnd,
  };
}

function montarRankings(input: {
  porUsuario: RelatorioTemposUsuario[];
  porItem: RelatorioTemposItem[];
  porSetor: RelatorioTemposSetor[];
}): NonNullable<RelatorioTemposResponse["rankings"]> {
  const ritmoUsuarioUnd = input.porUsuario.flatMap((u) => {
    const byUnd = new Map<string, number[]>();
    for (const item of u.porItem ?? []) {
      if (
        item.ritmoPorHora == null ||
        item.ritmoPorHora <= 0 ||
        !item.undNome
      ) {
        continue;
      }
      const lista = byUnd.get(item.undNome) ?? [];
      lista.push(item.ritmoPorHora);
      byUnd.set(item.undNome, lista);
    }
    return Array.from(byUnd.entries()).map(([undNome, ritmos]) => ({
      userId: u.userId,
      userNome: u.userNome,
      ocorrencias: u.ocorrencias,
      ritmoMedio: ritmos.reduce((a, b) => a + b, 0) / ritmos.length,
      ritmoUnd: undNome,
      ritmoAmostras: ritmos.length,
    }));
  });

  return {
    usuariosMaisRapidos: rankingDe(input.porUsuario, {
      id: (u) => u.userId,
      nome: (u) => u.userNome,
      valor: (u) => u.tempoMedioMs,
      format: (_u, v) => formatTempoMs(v),
      detalhe: (u) => `${u.ocorrencias} cronometragens`,
      ocorrencias: (u) => u.ocorrencias,
      ordem: "asc",
    }),
    usuariosMaisProdutivos: rankingDe(input.porUsuario, {
      id: (u) => u.userId,
      nome: (u) => u.userNome,
      valor: (u) => u.ocorrencias,
      format: (_u, v) => `${v} itens`,
      detalhe: (u) =>
        u.producaoPorUnd?.map((p) => p.totalFormatado).join(" · ") ||
        u.tempoFormatado,
      ocorrencias: (u) => u.ocorrencias,
      ordem: "desc",
    }),
    usuariosMelhorRitmo: rankingPorUnd(ritmoUsuarioUnd, (u) => u.ritmoUnd, {
      id: (u) => u.userId,
      nome: (u) => u.userNome,
      valor: (u) => u.ritmoMedio,
      format: (u, v) =>
        `${v.toLocaleString("pt-BR", {
          maximumFractionDigits: v >= 10 ? 1 : 2,
        })} ${u.ritmoUnd}/h`,
      detalhe: (u) =>
        `média entre ${u.ritmoAmostras} ${u.ritmoAmostras === 1 ? "item" : "itens"}`,
      ocorrencias: (u) => u.ocorrencias,
      ordem: "desc",
    }),
    itensMaisRapidos: rankingDe(input.porItem, {
      id: (i) => i.itemId,
      nome: (i) => i.descricao,
      valor: (i) => i.tempoMedioMs,
      format: (_i, v) => formatTempoMs(v),
      detalhe: (i) => `${i.ocorrencias} vezes`,
      ocorrencias: (i) => i.ocorrencias,
      ordem: "asc",
    }),
    itensMaisLentos: rankingDe(input.porItem, {
      id: (i) => i.itemId,
      nome: (i) => i.descricao,
      valor: (i) => i.tempoMedioMs,
      format: (_i, v) => formatTempoMs(v),
      detalhe: (i) => `${i.ocorrencias} vezes`,
      ocorrencias: (i) => i.ocorrencias,
      ordem: "desc",
    }),
    itensMaiorProducao: rankingPorUnd(
      input.porItem.filter((i) => i.producao != null && i.producao > 0),
      (i) => i.undNome,
      {
        id: (i) => i.itemId,
        nome: (i) => i.descricao,
        valor: (i) => i.producao ?? 0,
        format: (i) => i.producaoFormatada ?? String(i.producao),
        detalhe: (i) =>
          [
            i.tempoMedioFormatado ? `médio ${i.tempoMedioFormatado}` : "",
            i.ritmoFormatado,
          ]
            .filter(Boolean)
            .join(" · ") || i.tempoFormatado,
        ocorrencias: (i) => i.ocorrencias,
        ordem: "desc",
      }
    ),
    setoresMaisRapidos: rankingDe(input.porSetor, {
      id: (s) => s.setorId,
      nome: (s) => s.setorNome,
      valor: (s) => s.tempoMedioMs,
      format: (_s, v) => formatTempoMs(v),
      detalhe: (s) => `${s.ocorrencias} cronometragens`,
      ocorrencias: (s) => s.ocorrencias,
      ordem: "asc",
    }),
    setoresMaiorVolume: rankingDe(input.porSetor, {
      id: (s) => s.setorId,
      nome: (s) => s.setorNome,
      valor: (s) => s.ocorrencias,
      format: (_s, v) => `${v} itens`,
      detalhe: (s) => s.tempoFormatado,
      ocorrencias: (s) => s.ocorrencias,
      ordem: "desc",
    }),
    setoresMaiorEntrega: rankingDe(
      input.porSetor.filter((s) => (s.itensEsperados ?? 0) > 0),
      {
        id: (s) => s.setorId,
        nome: (s) => s.setorNome,
        valor: (s) => s.percentualEntrega ?? 0,
        format: (_s, v) => `${v}%`,
        detalhe: (s) =>
          `${s.itensEntregues ?? 0}/${s.itensEsperados ?? 0} itens no período`,
        ocorrencias: (s) => s.itensEsperados ?? 0,
        ordem: "desc",
      }
    ),
    usuariosMaisPausados: rankingDe(
      input.porUsuario.filter((u) => (u.tempoPausaMs ?? 0) > 0),
      {
        id: (u) => u.userId,
        nome: (u) => u.userNome,
        valor: (u) => u.tempoPausaMs ?? 0,
        format: (_u, v) => formatTempoMs(v),
        detalhe: (u) =>
          `produção ${u.tempoFormatado} · ${u.ocorrencias} cronometragens`,
        ocorrencias: (u) => u.ocorrencias,
        ordem: "desc",
      }
    ),
    itensMaisPausados: rankingDe(
      input.porItem.filter((i) => (i.tempoPausaMs ?? 0) > 0),
      {
        id: (i) => i.itemId,
        nome: (i) => i.descricao,
        valor: (i) => i.tempoPausaMs ?? 0,
        format: (_i, v) => formatTempoMs(v),
        detalhe: (i) => `produção ${i.tempoFormatado} · ${i.ocorrencias} vezes`,
        ocorrencias: (i) => i.ocorrencias,
        ordem: "desc",
      }
    ),
  };
}

export function aplicarFiltrosRelatorioTempos(
  relatorio: RelatorioTemposResponse,
  filtros: FiltrosRelatorioTempos
): {
  setores: RelatorioTemposSetor[];
  itens: RelatorioTemposItem[];
  usuarios: RelatorioTemposUsuario[];
  linhas: RelatorioTemposLinha[];
  entregaPorSetor: RelatorioTemposSetorEntrega[];
  rankings: NonNullable<RelatorioTemposResponse["rankings"]> | undefined;
} {
  const semFiltro =
    filtros.setorId === "" &&
    filtros.userId === "" &&
    filtros.itemId === "" &&
    !filtros.undNome;

  const setorNome =
    filtros.setorId === ""
      ? undefined
      : relatorio.porSetor?.find((s) => s.setorId === filtros.setorId)
          ?.setorNome;

  const setores = (relatorio.porSetor ?? []).filter((s) => {
    if (filtros.setorId !== "" && s.setorId !== filtros.setorId) return false;
    return true;
  });

  const itens = (relatorio.porItem ?? []).filter((item) => {
    if (filtros.itemId !== "" && item.itemId !== filtros.itemId) return false;
    if (filtros.undNome && rotuloUnd(item.undNome) !== filtros.undNome) {
      return false;
    }
    if (setorNome && !item.setores.includes(setorNome)) return false;
    if (filtros.userId !== "") {
      const usuario = relatorio.porUsuario.find(
        (u) => u.userId === filtros.userId
      );
      return (usuario?.porItem ?? []).some((i) => i.itemId === item.itemId);
    }
    return true;
  });

  const usuarios = relatorio.porUsuario
    .filter((u) => filtros.userId === "" || u.userId === filtros.userId)
    .map((u) => {
      const porItem = (u.porItem ?? []).filter((item) =>
        itemPassaFiltro(item, filtros, setorNome)
      );
      if (semFiltro) {
        return u;
      }
      return recomputarUsuario(u, porItem);
    })
    .filter((u) => semFiltro || (u.porItem ?? []).length > 0);

  const linhas = (relatorio.linhas ?? []).filter((l) => {
    if (filtros.setorId !== "" && l.setorId !== filtros.setorId) return false;
    if (filtros.itemId !== "" && l.itemId !== filtros.itemId) return false;
    if (filtros.userId !== "" && l.userId !== filtros.userId) return false;
    if (filtros.undNome && rotuloUnd(l.undNome) !== filtros.undNome) {
      return false;
    }
    return true;
  });

  const entregaPorSetor = (relatorio.entregaPorSetor ?? []).filter((s) => {
    if (filtros.setorId !== "" && s.setorId !== filtros.setorId) return false;
    return true;
  });

  return {
    setores,
    itens,
    usuarios,
    linhas,
    entregaPorSetor,
    rankings: semFiltro
      ? relatorio.rankings
      : montarRankings({
          porUsuario: usuarios,
          porItem: itens,
          porSetor: setores,
        }),
  };
}
